// Cards / KPI visual — a coloured tile per group/row with an optional
// micro-visual (trend line, spark bars, donut breakup, gauge, radial ring or
// progress bar) whose placement is chosen automatically from its type.
//
// Everything the card does is EXPLICIT via catalog options (nothing is
// inferred unless an option is left on `auto`):
//   • microVisual         none|sparkline|sparkbars|donut|gauge|ring|progressbar
//   • cardMode            auto|one-per-row|group|one-per-measure|single
//   • groupByColumns      which column(s) define one card (placed OR not)
//   • headlineAgg         auto|last|sum|avg|min|max|count
//   • trend/breakup/gauge column bindings
//   • graphicPosition     auto|right|bottom|off  (auto ⇒ donut/gauge/ring=right
//                          with the text shifting left; trend/progress=bottom
//                          full-width on a narrow card, right once the card is
//                          wide enough to seat them beside the text)
//   • slotFlow            auto|inline|stack — whether a slot's columns stack
//                          (a line each) or share ONE line, so `qty`, `' / '`,
//                          `total` reads as "10 / 25"
// `auto` reproduces the historical behaviour: legacy `sparkline:true` maps to a
// bottom-placed trend line grouped by the non-measure/non-date layout columns.
//
// Text is white on the coloured card, so micro-visuals are drawn in shades of
// translucent white — theme-agnostic and legible on every palette colour.
//
// Icon FORMAT columns degrade to text — twasta has no CustomMenuImageServlet,
// so we render the value rather than fetching an image that would 404.

import type { CSSProperties, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { VisualOption } from './types'
import type { NativeRendererProps } from './nativeRenderers'
import { humanizeColumnLabel, mapVisualData, pickGroup, measures, toNumber, type MappedColumn, type MappedData } from './dataUtils'
import { formatCell } from './formatters'
import { readStrOption, readNumOption, readBoolOption } from './optionUtils'
import { displayPlainText, hasRichDisplay } from './displayCell'
import { useRowSelection } from './selection'

// Colour sets lifted from insight-card.component.ts (obvious hex typos fixed).
const CARD_PALETTES: Record<string, string[]> = {
  Pink: ['#FB9898', '#FBA2A2', '#FCADAD', '#FCB7B7', '#FDC1C1', '#FDCBCB', '#FDD606'],
  Purple: ['#B29ECA', '#BAABCF', '#C1B1D5', '#C9BBDA', '#D1C5DF', '#D8CEE4', '#E0D8EA'],
  Orange: ['#ED9768', '#EFA177', '#F1AC86', '#F2B695', '#F4C1A4', '#F6CBB3', '#F8D5C3'],
  SkyBlue: ['#22BBE9', '#38BFEB', '#4EC6ED', '#64CDF0', '#7AD4F2', '#90DBF4', '#A7E3F6'],
  Green: ['#3EB390', '#51BA9B', '#65C2A6', '#7BCAB1', '#8BD1BC', '#9ED9C7', '#B2E1D3'],
  Yellow: ['#F9AB03', '#FAB11C', '#FAB935', '#FBC24E', '#FBCB68', '#FCD381', '#FDDC9A'],
  DarkBlue: ['#0AB4FF', '#229DFF', '#3B9DFF', '#53A9FF', '#6CB5FF', '#84C1FF', '#9DCEFF'],
  'Yellow-SkyBlue': ['#F9AB03', '#22BBE9'],
  'DarkBlue-Yellow': ['#0AB4FF', '#F9AB03'],
  'Purple-Pink': ['#B29ECA', '#FB9898'],
  'SkyBlue-Pink': ['#22BBE9', '#FB9898'],
  'Green-Pink': ['#3EB390', '#FB9898'],
  'Yellow-Pink': ['#F9AB03', '#FB9898'],
  'DarkBlue-Pink': ['#0AB4FF', '#FB9898'],
  'Green-SkyBlue': ['#3EB390', '#22BBE9'],
  'Orange-Purple': ['#ED9768', '#B29ECA'],
  'SkyBlue-Purple': ['#22BBE9', '#B29ECA'],
  'Green-Purple': ['#3EB390', '#B29ECA'],
  'Yellow-Purple': ['#F9AB03', '#B29ECA'],
  'DarkBlue-Purple': ['#0AB4FF', '#B29ECA'],
  'Yellow-Green': ['#F9AB03', '#3EB390'],
  'DarkBlue-Green': ['#0AB4FF', '#3EB390'],
  'SkyBlue-Orange': ['#22BBE9', '#ED9768'],
  'Green-Orange': ['#3EB390', '#ED9768'],
  'DarkBlue-Orange': ['#0AB4FF', '#ED9768'],
  'Yellow-SkyBlue-Pink': ['#F9AB03', '#22BBE9', '#FB9898'],
  'Green-SkyBlue-Pink': ['#3EB390', '#22BBE9', '#FB9898'],
  'Blue-Orange-Violet-Green': [
    'linear-gradient(-45deg, #007bab, #00c9ff)',
    'linear-gradient(-45deg, #e47842, #ffac83)',
    'linear-gradient(-45deg, #7053cc, #c3b3f7)',
    'linear-gradient(-45deg, #01a27f, #00e1af)',
  ],
}
const DEFAULT_PALETTE = ['#22BBE9', '#3EB390', '#F9AB03', '#B29ECA', '#ED9768', '#0AB4FF', '#FB9898']

// Baseline card height so cards are identical with or without a micro-visual
// (the graphic lives inside the flex:1 centre band, absorbed rather than added).
// It is a MINIMUM, not a cap: a card whose slots carry several lines each grows
// instead of clipping its centre band mid-glyph, and flex stretches the rest of
// the row to match.
const CARD_HEIGHT = 164
// …and a CEILING. A card is a KPI tile, not a panel: in a tall host (a full-page
// visual preview, a stretched dashboard row) an uncapped `height: 100%` blew the
// card up to the whole viewport with its content marooned in the middle. It
// grows to fill only up to this, then stops and leaves the slack empty.
const CARD_MAX_HEIGHT = 260

// Card width is RESOLVED from the host, not fixed: the cards on a row share it
// evenly and grow into spare width instead of leaving a blank gutter beside
// them. CARD_TRACK is the width below which another card no longer fits on the
// row; CARD_MAX_WIDTH is only the fallback for hosts we could not measure.
const CARD_TRACK = 260
const CARD_MIN_WIDTH = 200
const CARD_MAX_WIDTH = 560
// From this width on, a card is wide enough to seat the graphic BESIDE the text
// (rather than as a strip under it) without squeezing either.
const WIDE_CARD = 340
const CARD_GAP = 10
const CARD_PAD = 10

// Translucent-white shades for donut slices (monochrome-on-colour aesthetic).
const SLICE_ALPHAS = [0.95, 0.82, 0.68, 0.56, 0.46, 0.38, 0.31, 0.25]
const TRACK = 'rgba(255,255,255,0.25)'
const FILL = 'rgba(255,255,255,0.92)'

function paletteFromOptions(options: VisualOption[] | undefined): string[] {
  const opt = (options ?? []).find(
    (o) => o.optionName === 'cardColorPallete' || o.optionId === 'cardColorPallete',
  )
  const key = opt?.defaultValue
  if (key && CARD_PALETTES[key]) return CARD_PALETTES[key]
  return DEFAULT_PALETTE
}

// ── column helpers ───────────────────────────────────────────────────────────
const isMeasureCol = (c: MappedColumn) => c.type === 'number' || c.coltype === 'NUMBER'
const isDateCol = (c: MappedColumn) =>
  c.type === 'datetime' || c.type === 'date' || c.coltype === 'DATE'

function allMappedCols(md: MappedData): MappedColumn[] {
  const out: MappedColumn[] = []
  for (const k of Object.keys(md.groups)) out.push(...md.groups[k])
  return out
}

/** Resolve an option-supplied column name to the actual row key (case-
 *  insensitive) — the column need NOT be placed on a card slot, it only has to
 *  exist in the query result. */
function resolveKey(md: MappedData, name: string | undefined): string | undefined {
  if (!name) return undefined
  const lc = name.toUpperCase()
  const schemaKeys = Object.keys(md.schema || {})
  const hit = schemaKeys.find((k) => k.toUpperCase() === lc)
  if (hit) return hit
  const first = md.rows[0] || {}
  return Object.keys(first).find((k) => k.toUpperCase() === lc)
}

/** Resolve an option-supplied column name to a MappedColumn (uses the placed
 *  column's metadata when available, else a bare column from the schema). */
function colByName(md: MappedData, name: string | undefined): MappedColumn | undefined {
  const key = resolveKey(md, name)
  if (!key) return undefined
  const up = key.toUpperCase()
  for (const c of allMappedCols(md)) if (c.name.toUpperCase() === up) return c
  return { name: key, label: humanizeColumnLabel(key), type: md.schema[key]?.type }
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n
}

function aggregate(values: number[], fn: string): number {
  if (!values.length) return 0
  switch (fn) {
    case 'sum': return values.reduce((a, b) => a + b, 0)
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length
    case 'min': return Math.min(...values)
    case 'max': return Math.max(...values)
    case 'count': return values.length
    case 'last': default: return values[values.length - 1]
  }
}

/** Stable sort a copy of the rows by a column (numeric when possible, else by
 *  string/date). */
function sortRowsBy(rows: Array<Record<string, unknown>>, col: MappedColumn | undefined): Array<Record<string, unknown>> {
  if (!col) return rows
  const withIdx = rows.map((r, i) => ({ r, i }))
  withIdx.sort((a, b) => {
    const va = a.r[col.name]
    const vb = b.r[col.name]
    const na = Number(va)
    const nb = Number(vb)
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb || a.i - b.i
    const sa = String(va ?? '')
    const sb = String(vb ?? '')
    return sa < sb ? -1 : sa > sb ? 1 : a.i - b.i
  })
  return withIdx.map((x) => x.r)
}

// ── micro-visual spec + builders ─────────────────────────────────────────────
type MicroType = 'none' | 'sparkline' | 'sparkbars' | 'donut' | 'gauge' | 'ring' | 'progressbar'
type CardMode = 'one-per-row' | 'group' | 'one-per-measure' | 'single'

interface Slice { label: string; value: number }
type MicroSpec =
  | { kind: 'line' | 'bars'; values: number[] }
  | { kind: 'donut'; slices: Slice[] }
  | { kind: 'gauge' | 'ring' | 'progress'; value: number; min: number; max: number }
  | null

interface MicroCtx {
  md: MappedData
  options: VisualOption[] | undefined
  aggFn: string
  headlineMeasure: MappedColumn | undefined // center-first-row measure (fallback binding)
  dimCols: MappedColumn[]                    // layout dimension cols (fallback breakdown)
}

function buildMicro(micro: MicroType, rows: Array<Record<string, unknown>>, ctx: MicroCtx): MicroSpec {
  if (micro === 'none' || !rows.length) return null
  const { md, options, headlineMeasure, dimCols } = ctx

  if (micro === 'sparkline' || micro === 'sparkbars') {
    const valCol =
      colByName(md, readStrOption(options, 'trendValueColumn')) ?? headlineMeasure ?? measures(md)[0]
    if (!valCol) return null
    const orderCol = colByName(md, readStrOption(options, 'trendOrderColumn'))
    const ordered = sortRowsBy(rows, orderCol)
    const values = ordered.map((r) => Number(r[valCol.name])).filter((n) => Number.isFinite(n))
    if (values.length < 2) return null
    return { kind: micro === 'sparkbars' ? 'bars' : 'line', values }
  }

  if (micro === 'donut') {
    const bdCol =
      colByName(md, readStrOption(options, 'breakdownColumn')) ?? dimCols.find((c) => !isDateCol(c)) ?? dimCols[0]
    const valCol =
      colByName(md, readStrOption(options, 'breakdownValueColumn')) ?? headlineMeasure ?? measures(md)[0]
    if (!bdCol) return null
    const order: string[] = []
    const sums = new Map<string, number>()
    for (const r of rows) {
      const label = String(r[bdCol.name] ?? '')
      const v = valCol ? toNumber(r[valCol.name]) : 1
      if (!sums.has(label)) { sums.set(label, 0); order.push(label) }
      sums.set(label, sums.get(label)! + v)
    }
    let slices: Slice[] = order.map((label) => ({ label, value: sums.get(label)! }))
    slices.sort((a, b) => b.value - a.value)
    const maxSlices = Math.max(1, Math.round(readNumOption(options, 'maxSlices', 6)))
    if (slices.length > maxSlices) {
      const head = slices.slice(0, maxSlices - 1)
      const rest = slices.slice(maxSlices - 1).reduce((s, x) => s + x.value, 0)
      slices = [...head, { label: 'Other', value: rest }]
    }
    slices = slices.filter((s) => s.value > 0)
    if (!slices.length) return null
    return { kind: 'donut', slices }
  }

  // gauge / ring / progressbar — a single aggregated value against a range.
  const valCol =
    colByName(md, readStrOption(options, 'gaugeValueColumn')) ?? headlineMeasure ?? measures(md)[0]
  if (!valCol) return null
  // A gauge over a multi-row group defaults to the TOTAL; an explicit
  // headlineAgg (other than the auto/last default) overrides that.
  const gaugeFn = ctx.aggFn === 'last' ? 'sum' : ctx.aggFn
  const value = aggregate(rows.map((r) => Number(r[valCol.name])).filter((n) => Number.isFinite(n)), gaugeFn)
  const min = readNumOption(options, 'gaugeMin', 0)
  const maxRaw = readStrOption(options, 'gaugeMax')
  const max = maxRaw ? Number(maxRaw) : NaN // NaN ⇒ resolved later against all cards
  const kind = micro === 'progressbar' ? 'progress' : micro
  return { kind, value, min, max }
}

// ── micro-visual components ──────────────────────────────────────────────────
// `stretch` (bottom placement) renders the SVG at 100% card width; the viewBox
// keeps the drawing coordinates so only the horizontal axis scales.
function spanStyle(stretch?: boolean): CSSProperties {
  return stretch ? { ...svgStyle, width: '100%', marginTop: 4 } : svgStyle
}

function Sparkline({ values, w, h, stretch }: { values: number[]; w: number; h: number; stretch?: boolean }) {
  if (!values || values.length < 2) return null
  const pad = 2
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const step = (w - pad * 2) / (values.length - 1)
  const points = values
    .map((v, i) => {
      const x = pad + i * step
      const y = h - pad - ((v - min) / span) * (h - pad * 2)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg width={stretch ? '100%' : w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio={stretch ? 'none' : undefined} style={spanStyle(stretch)} aria-hidden="true">
      <polyline points={points} fill="none" stroke={FILL} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

function SparkBars({ values, w, h, stretch }: { values: number[]; w: number; h: number; stretch?: boolean }) {
  if (!values || values.length < 1) return null
  const pad = 1
  const max = Math.max(...values, 0)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const zeroY = h - pad - ((0 - min) / span) * (h - pad * 2)
  const slot = (w - pad * 2) / values.length
  const bw = Math.max(1, slot * 0.7)
  return (
    <svg width={stretch ? '100%' : w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio={stretch ? 'none' : undefined} style={spanStyle(stretch)} aria-hidden="true">
      {values.map((v, i) => {
        const y = h - pad - ((v - min) / span) * (h - pad * 2)
        const top = Math.min(y, zeroY)
        const bh = Math.max(1, Math.abs(zeroY - y))
        const x = pad + i * slot + (slot - bw) / 2
        return <rect key={i} x={x} y={top} width={bw} height={bh} rx={0.6} fill={FILL} />
      })}
    </svg>
  )
}

function Donut({ slices, size }: { slices: Slice[]; size: number }) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1
  const sw = Math.max(4, size * 0.16)
  const r = (size - sw) / 2
  const c = 2 * Math.PI * r
  const cx = size / 2
  let offset = 0
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle} aria-hidden="true">
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={TRACK} strokeWidth={sw} />
      {slices.map((s, i) => {
        const frac = s.value / total
        const dash = frac * c
        const el = (
          <circle
            key={i}
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={`rgba(255,255,255,${SLICE_ALPHAS[i % SLICE_ALPHAS.length]})`}
            strokeWidth={sw}
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        )
        offset += dash
        return el
      })}
    </svg>
  )
}

function Gauge({ value, min, max, size }: { value: number; min: number; max: number; size: number }) {
  const w = size
  const h = size * 0.62
  const cx = w / 2
  const cy = h - 4
  const r = w / 2 - 6
  const frac = clamp(max > min ? (value - min) / (max - min) : 0, 0, 1)
  const arcLen = Math.PI * r
  const dash = frac * arcLen
  const path = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`
  const theta = Math.PI * (1 - frac) // 180°→0°
  const tipX = cx + r * Math.cos(theta)
  const tipY = cy - r * Math.sin(theta)
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={svgStyle} aria-hidden="true">
      <path d={path} fill="none" stroke={TRACK} strokeWidth={6} strokeLinecap="round" />
      <path d={path} fill="none" stroke={FILL} strokeWidth={6} strokeLinecap="round" strokeDasharray={`${dash} ${arcLen}`} />
      <line x1={cx} y1={cy} x2={tipX} y2={tipY} stroke={FILL} strokeWidth={2} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={2.5} fill={FILL} />
    </svg>
  )
}

function Ring({ value, min, max, size }: { value: number; min: number; max: number; size: number }) {
  const sw = Math.max(4, size * 0.14)
  const r = (size - sw) / 2
  const c = 2 * Math.PI * r
  const cx = size / 2
  const frac = clamp(max > min ? (value - min) / (max - min) : 0, 0, 1)
  const dash = frac * c
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle} aria-hidden="true">
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={TRACK} strokeWidth={sw} />
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        stroke={FILL}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${cx} ${cx})`}
      />
      <text x={cx} y={cx} dy="0.35em" textAnchor="middle" fill="#fff" fontSize={size * 0.24} fontWeight={700}>
        {Math.round(frac * 100)}%
      </text>
    </svg>
  )
}

function ProgressBar({ value, min, max, w, stretch }: { value: number; min: number; max: number; w: number; stretch?: boolean }) {
  const h = 10
  const frac = clamp(max > min ? (value - min) / (max - min) : 0, 0, 1)
  return (
    <svg width={stretch ? '100%' : w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio={stretch ? 'none' : undefined} style={spanStyle(stretch)} aria-hidden="true">
      <rect x={0} y={2} width={w} height={h - 4} rx={3} fill={TRACK} />
      <rect x={0} y={2} width={Math.max(2, frac * w)} height={h - 4} rx={3} fill={FILL} />
    </svg>
  )
}

const svgStyle: CSSProperties = { display: 'block', maxWidth: '100%', flexShrink: 0 }

/** Where a micro-visual sits, resolved from its type + the graphicPosition
 *  option. `right` ⇒ split card (text left, graphic right); `bottom` ⇒ graphic
 *  as a strip under the centre text. */
function resolvePlacement(
  micro: MicroType,
  options: VisualOption[] | undefined,
  cardW = 0,
): 'right' | 'bottom' | 'off' {
  if (micro === 'none') return 'off'
  const pos = readStrOption(options, 'graphicPosition', 'auto').toLowerCase()
  if (pos === 'off') return 'off'
  if (pos === 'right' || pos === 'bottom') return pos
  // auto: donut/gauge/ring always read best beside the text. Trend/progress span
  // the width on a narrow card, but once the card is wide the space is better
  // spent seating them beside the text than stretching them under it.
  if (micro === 'donut' || micro === 'gauge' || micro === 'ring') return 'right'
  return cardW >= WIDE_CARD ? 'right' : 'bottom'
}

/** The width one card gets: the host row split evenly between as many cards as
 *  fit on it, so cards absorb spare width instead of leaving a blank gutter. */
function cardWidthFor(hostW: number, count: number): number {
  if (!hostW || !count) return CARD_MAX_WIDTH
  const inner = Math.max(0, hostW - CARD_PAD * 2)
  const perRow = Math.max(1, Math.min(count, Math.floor((inner + CARD_GAP) / (CARD_TRACK + CARD_GAP))))
  const w = (inner - CARD_GAP * (perRow - 1)) / perRow
  // No upper cap: the row is SHARED, so the cards always add up to the host
  // width — a lone card in a wide tile fills it (with its graphic moving beside
  // the text) rather than leaving a blank gutter half the tile wide.
  return Math.max(w, CARD_MIN_WIDTH)
}

/** Live width of the card container (0 until measured / without ResizeObserver,
 *  in which case the layout falls back to the plain max-width behaviour). */
function useHostWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Measure once up front: layout is settled by the time effects run, so the
    // card is sized on the very next paint rather than waiting for the observer's
    // first delivery (which needs a frame, and may never come in a static host).
    const w0 = el.getBoundingClientRect().width
    if (w0) setWidth((prev) => (Math.abs(prev - w0) < 1 ? prev : w0))
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0
      setWidth((prev) => (Math.abs(prev - w) < 1 ? prev : w))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width]
}

function MicroGraphic({ spec, placement, cardW = 0 }: { spec: MicroSpec; placement: 'right' | 'bottom'; cardW?: number }) {
  if (!spec) return null
  const right = placement === 'right'
  const stretch = !right // bottom placement spans the card width
  // A right-placed graphic takes roughly a third of the card, so it grows with
  // the card instead of sitting as a fixed stamp in a wide, half-empty tile.
  const side = right ? clamp(Math.round(cardW * 0.32), 76, 180) : 0
  switch (spec.kind) {
    case 'line':
      return <Sparkline values={spec.values} w={right ? Math.max(100, side) : 260} h={right ? 44 : 26} stretch={stretch} />
    case 'bars':
      return <SparkBars values={spec.values} w={right ? Math.max(100, side) : 260} h={right ? 44 : 28} stretch={stretch} />
    case 'donut':
      return <Donut slices={spec.slices} size={right ? clamp(side, 76, 120) : 68} />
    case 'gauge':
      return <Gauge value={spec.value} min={spec.min} max={spec.max} size={right ? clamp(side, 84, 132) : 96} />
    case 'ring':
      return <Ring value={spec.value} min={spec.min} max={spec.max} size={right ? clamp(side, 74, 118) : 66} />
    case 'progress':
      return <ProgressBar value={spec.value} min={spec.min} max={spec.max} w={right ? Math.max(96, side) : 260} stretch={stretch} />
    default:
      return null
  }
}

// ── main component ───────────────────────────────────────────────────────────
interface CardEntry {
  row: Record<string, unknown>       // display row (aggregated for grouped cards)
  spec: MicroSpec
}

// ── slot flow (how a slot's columns share their line) ────────────────────────
// A slot normally STACKS its columns, one line each. But a slot often composes a
// single reading — `DONE_QTY`, `' / '`, `TOTAL_QTY` ⇒ "10 / 25" — where the text
// column is an inline separator, not a line of its own. `slotFlow` decides:
//   auto   a text column sitting AFTER a value column reads as a separator ⇒
//          the slot composes onto ONE line; otherwise the columns stack
//   inline / stack   force it
type SlotFlow = 'inline' | 'stack'

function slotFlowFor(cols: MappedColumn[], forced: string): SlotFlow {
  if (forced === 'inline' || forced === 'stack') return forced
  if (cols.length < 2) return 'stack'
  const firstVal = cols.findIndex(isMeasureCol)
  if (firstVal < 0) return 'stack'
  return cols.slice(firstVal + 1).some((c) => !isMeasureCol(c)) ? 'inline' : 'stack'
}

/** One card slot rendered: either a line per column (stacked) or every column on
 *  ONE line (inline), so a value/text/value trio reads as "10 / 25". */
function SlotLine({
  cols,
  row,
  textStyle,
  flow,
  align,
}: {
  cols: MappedColumn[]
  row: Record<string, unknown>
  textStyle: CSSProperties
  flow: SlotFlow
  align?: 'left' | 'center' | 'right'
}) {
  if (!cols.length) return null
  if (flow === 'stack') {
    return (
      <>
        {cols.map((c) => (
          <div key={c.name} style={textStyle}>{fmt(row, c)}</div>
        ))}
      </>
    )
  }
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'
  return (
    <div style={{ ...textStyle, display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: justify }}>
      {cols.map((c) => (
        <span key={c.name} style={{ whiteSpace: 'nowrap' }}>{fmt(row, c)}</span>
      ))}
    </div>
  )
}

export function CardsVisual({ jsonData, visualLayout, optionsArr, onSelect }: NativeRendererProps) {
  const { select, isSelected } = useRowSelection(onSelect)
  const [hostRef, hostW] = useHostWidth()
  const md = mapVisualData(jsonData, visualLayout)
  if (!md.rows.length) return <Empty text="No data." />

  const topLeft = visibleCols(pickGroup(md, 'topleft'))
  const topRight = visibleCols(pickGroup(md, 'topright'))
  const center1 = visibleCols(pickGroup(md, 'centerfirstrow'))
  const center2 = visibleCols(pickGroup(md, 'centersecondrow'))
  const botLeft = visibleCols(pickGroup(md, 'bottomleft'))
  const botRight = visibleCols(pickGroup(md, 'bottomright'))
  const allLayoutCols = [topLeft, topRight, center1, center2, botLeft, botRight].flat()
  const hasLayout = allLayoutCols.length > 0

  const options = (optionsArr ?? visualLayout.options) as VisualOption[] | undefined
  const palette = paletteFromOptions(visualLayout.options as VisualOption[] | undefined)

  // Resolve the effective micro-visual (auto ⇒ legacy sparkline flag).
  const microRaw = readStrOption(options, 'microVisual', 'auto').toLowerCase()
  const legacySpark = readBoolOption(options, 'sparkline')
  const micro: MicroType =
    microRaw === 'auto'
      ? (legacySpark ? 'sparkline' : 'none')
      : (['none', 'sparkline', 'sparkbars', 'donut', 'gauge', 'ring', 'progressbar'].includes(microRaw)
          ? (microRaw as MicroType)
          : 'none')
  // Whether a graphic is drawn at all is width-independent; WHERE it sits is
  // resolved per branch, once the card count (and so the card width) is known.
  const graphicOn = resolvePlacement(micro, options) !== 'off'

  const aggRaw = readStrOption(options, 'headlineAgg', 'auto').toLowerCase()
  const aggFn = aggRaw === 'auto' ? 'last' : aggRaw

  const headlineMeasure = center1.find(isMeasureCol) ?? measures(md)[0]
  const dimCols = allLayoutCols.filter((c) => !isMeasureCol(c))
  const microCtx: MicroCtx = { md, options, aggFn, headlineMeasure, dimCols }

  const modeRaw = readStrOption(options, 'cardMode', 'auto').toLowerCase()
  const forcedFlow = readStrOption(options, 'slotFlow', 'auto').toLowerCase()

  // ── No layout assigned → one card per measure (each spanning all rows) ──────
  // The ONLY case where a column (rather than a row/group) defines a card: with
  // nothing placed on the slots there is no card layout to fill, so each measure
  // becomes its own tile. With a layout, cards always come from ROWS — the slots
  // are positions on ONE card.
  if (!hasLayout) {
    const vals = measures(md)
    if (!vals.length) return <Empty text="No measures to display." />
    const raw = vals.map((col) => {
      const nums = md.rows.map((r) => Number(r[col.name])).filter((n) => Number.isFinite(n))
      const headline = aggregate(nums, aggRaw === 'auto' ? 'sum' : aggFn)
      const spec = graphicOn ? buildMicro(micro, md.rows, { ...microCtx, headlineMeasure: col }) : null
      return { col, headline, spec }
    })
    const cards: CardEntry[] = raw.map(({ col, headline, spec }) => ({
      row: { __value: headline, __label: col.label, __col: col },
      spec,
    }))
    resolveAutoMax(cards)
    const cardW = cardWidthFor(hostW, cards.length)
    const placement = resolvePlacement(micro, options, cardW)
    return (
      <div ref={hostRef} style={containerStyle}>
        {cards.map(({ row, spec }, i) => {
          const col = row.__col as MappedColumn
          const body = (
            <div style={centreBand}>
              <div style={center1Text}>{hasRichDisplay(col) ? displayPlainText(row.__value, col) : formatCell(row.__value, { type: 'number', format: col.format })}</div>
              <div style={center2Text}>{String(row.__label)}</div>
              {placement === 'bottom' && spec && <MicroGraphic spec={spec} placement="bottom" />}
            </div>
          )
          return (
            <div key={col.name} style={cardStyle(palette[i % palette.length], cardW)}>
              <div style={cardBody}>
                {placement === 'right' && spec
                  ? <div style={splitRow}><div style={splitText}>{body}</div><div style={splitGraphic}><MicroGraphic spec={spec} placement="right" cardW={cardW} /></div></div>
                  : body}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // ── Layout assigned → resolve card mode + grouping ──────────────────────────
  const explicitGroupBy = readStrOption(options, 'groupByColumns')
    .split(',').map((s) => s.trim()).filter(Boolean)
    .map((n) => colByName(md, n)).filter((c): c is MappedColumn => !!c)

  let mode: CardMode
  // `one-per-measure` is a NO-LAYOUT mode (handled above): once slots are bound
  // they are positions on ONE card, so it degrades to one card per row here.
  if (['one-per-row', 'group', 'one-per-measure', 'single'].includes(modeRaw)) {
    mode = modeRaw as CardMode
  } else {
    // auto: grouping when a micro-visual (or legacy spark) or explicit group-by
    // is in play, else the historical one-card-per-row.
    mode = micro !== 'none' || explicitGroupBy.length ? 'group' : 'one-per-row'
  }

  // Columns whose distinct combinations define a card (group mode).
  const keyCols = explicitGroupBy.length
    ? explicitGroupBy
    : allLayoutCols.filter((c) => !isMeasureCol(c) && !isDateCol(c))

  const groups = buildGroups(md.rows, mode, keyCols)
  const layoutMeasures = allLayoutCols.filter(isMeasureCol)

  const cards: CardEntry[] = groups.map((grp) => {
    const last = grp[grp.length - 1]
    let display = last
    if (grp.length > 1 && aggFn !== 'last') {
      display = { ...last }
      for (const c of layoutMeasures) {
        const nums = grp.map((r) => Number(r[c.name])).filter((n) => Number.isFinite(n))
        display[c.name] = aggregate(nums, aggFn)
      }
    }
    const spec = graphicOn ? buildMicro(micro, grp, microCtx) : null
    return { row: display, spec }
  })
  resolveAutoMax(cards)
  const cardW = cardWidthFor(hostW, cards.length)
  const placement = resolvePlacement(micro, options, cardW)

  return (
    <div ref={hostRef} style={containerStyle}>
      {cards.map(({ row, spec }, ri) => {
        const sel = isSelected(row)
        const leftAlign = placement === 'right'
        const bands = (
          <>
            {(topLeft.length || topRight.length) > 0 && (
              <div style={topBand}>
                <div style={topLeftCol}><SlotLine cols={topLeft} row={row} textStyle={topText} flow={slotFlowFor(topLeft, forcedFlow)} /></div>
                <div style={topRightCol}><SlotLine cols={topRight} row={row} textStyle={{ ...topText, textAlign: 'right' }} flow={slotFlowFor(topRight, forcedFlow)} align="right" /></div>
              </div>
            )}
            <div style={leftAlign ? centreBandLeft : centreBand}>
              <SlotLine cols={center1} row={row} textStyle={leftAlign ? { ...center1Text, textAlign: 'left' } : center1Text} flow={slotFlowFor(center1, forcedFlow)} align={leftAlign ? 'left' : 'center'} />
              <SlotLine cols={center2} row={row} textStyle={leftAlign ? { ...center2Text, textAlign: 'left' } : center2Text} flow={slotFlowFor(center2, forcedFlow)} align={leftAlign ? 'left' : 'center'} />
              {placement === 'bottom' && spec && <MicroGraphic spec={spec} placement="bottom" />}
            </div>
            {(botLeft.length || botRight.length) > 0 && (
              <div style={bottomBand}>
                <div style={bottomLeftCol}><SlotLine cols={botLeft} row={row} textStyle={bottomText} flow={slotFlowFor(botLeft, forcedFlow)} /></div>
                <div style={bottomRightCol}><SlotLine cols={botRight} row={row} textStyle={{ ...bottomText, textAlign: 'right' }} flow={slotFlowFor(botRight, forcedFlow)} align="right" /></div>
              </div>
            )}
          </>
        )
        return (
          <div
            key={ri}
            onClick={select ? () => select(row) : undefined}
            aria-pressed={select ? sel : undefined}
            style={{
              ...cardStyle(palette[ri % palette.length], cardW),
              cursor: select ? 'pointer' : undefined,
              boxShadow: sel
                ? '0 0 0 3px rgba(255,255,255,0.55), 0 10px 24px rgba(15,23,42,0.28)'
                : cardStyle(palette[ri % palette.length], cardW).boxShadow,
            }}
          >
            <div style={cardBody}>
              {placement === 'right' && spec
                ? <div style={splitRow}><div style={splitText}>{bands}</div><div style={splitGraphic}><MicroGraphic spec={spec} placement="right" cardW={cardW} /></div></div>
                : bands}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Group the rows for the chosen card mode. */
function buildGroups(
  rows: Array<Record<string, unknown>>,
  mode: CardMode,
  keyCols: MappedColumn[],
): Array<Array<Record<string, unknown>>> {
  if (mode === 'one-per-row' || mode === 'one-per-measure') return rows.map((r) => [r])
  if (mode === 'single') return [rows]
  // group
  const order: string[] = []
  const map = new Map<string, Array<Record<string, unknown>>>()
  for (const row of rows) {
    const key = keyCols.map((c) => String(row[c.name] ?? '')).join('')
    if (!map.has(key)) { map.set(key, []); order.push(key) }
    map.get(key)!.push(row)
  }
  return order.map((k) => map.get(k)!)
}

/** Fill in `max: NaN` on gauge/ring/progress specs with the largest value across
 *  all cards (so bare values compare relatively when no explicit max is set). */
function resolveAutoMax(cards: CardEntry[]): void {
  let peak = 0
  for (const c of cards) {
    if (c.spec && (c.spec.kind === 'gauge' || c.spec.kind === 'ring' || c.spec.kind === 'progress')) {
      if (Number.isNaN(c.spec.max)) peak = Math.max(peak, c.spec.value)
    }
  }
  const auto = peak > 0 ? peak : 1
  for (const c of cards) {
    if (c.spec && (c.spec.kind === 'gauge' || c.spec.kind === 'ring' || c.spec.kind === 'progress')) {
      if (Number.isNaN(c.spec.max)) c.spec.max = auto
    }
  }
}

function visibleCols(cols: MappedColumn[]): MappedColumn[] {
  return cols.filter((c) => !c.hidden)
}

function fmt(row: Record<string, unknown>, c: MappedColumn): string {
  // Cards are white-on-colour, so rich React blocks (data bars, pills) would
  // clash — use the PLAIN-TEXT rendering of the display format (number / date /
  // status label / template …), falling back to the legacy mask formatter.
  if (hasRichDisplay(c)) return displayPlainText(row[c.name], c, row)
  return formatCell(row[c.name], { type: c.type, coltype: c.coltype, format: c.format, pattern: c.pattern })
}

function Empty({ text }: { text: string }) {
  return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, #888)', textAlign: 'center' }}>{text}</div>
}

// ── styles (mirroring insight-card.component.css) ────────────────────────────
const containerStyle: CSSProperties = {
  width: '100%', height: '100%', padding: 10, display: 'flex', flexWrap: 'wrap',
  gap: 10, alignContent: 'flex-start', overflow: 'auto', boxSizing: 'border-box',
}
// A card FITS its host: `height: 100%` fills the space the dashboard tile gives
// it, and the CARD_HEIGHT baseline is written `min(CARD_HEIGHT, 100%)` so it
// yields when the tile is shorter than that — a hard floor is what pushed the
// card past a short tile and let the tile's `overflow: hidden` slice its bottom
// edge (rounded corners and all). If the card's own content still can't fit, it
// scrolls inside the card rather than being cut.
// Height is likewise bounded ABOVE by CARD_MAX_HEIGHT so a tall host leaves the
// slack empty instead of stretching one KPI tile down the page, and `maxWidth`
// is the resolved share of the row so cards spread into spare width.
const cardStyle = (bg: string, maxW: number = CARD_MAX_WIDTH): CSSProperties => ({
  flex: '1 1 220px', minWidth: CARD_MIN_WIDTH, maxWidth: maxW,
  height: '100%', minHeight: `min(${CARD_HEIGHT}px, 100%)`, maxHeight: CARD_MAX_HEIGHT,
  borderRadius: 10, display: 'flex', flexDirection: 'column', position: 'relative',
  color: '#fff', fontWeight: 700, background: bg, overflowX: 'hidden', overflowY: 'auto',
  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
})
// Inner body: lays the bands out. No height floor of its own — the card owns it.
const cardBody: CSSProperties = {
  flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
}
// Split card: text column on the left, graphic pinned right + vertically centred.
const splitRow: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', minHeight: 0 }
const splitText: CSSProperties = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }
const splitGraphic: CSSProperties = { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px 0 6px' }
const topBand: CSSProperties = { display: 'flex', gap: 20, minHeight: 24, flexShrink: 0, alignItems: 'flex-start', padding: '10px 12px 0' }
const topLeftCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }
const topRightCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0, alignItems: 'flex-end' }
const topText: CSSProperties = { flexShrink: 0, fontSize: 18, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }
// `1 0 auto`: the centre band still absorbs spare height (grow), but its basis
// is its CONTENT and it never shrinks. With `flex: 1` (basis 0, shrink 1) a card
// whose top/bottom bands are full left the centre band shorter than its own
// lines — and since the band centres its content, the overflow was clipped
// symmetrically, slicing the headline through the middle.
const centreBand: CSSProperties = { flex: '1 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px 12px', gap: 2, minHeight: 0 }
const centreBandLeft: CSSProperties = { ...centreBand, alignItems: 'flex-start', padding: '4px 4px 4px 14px' }
const center1Text: CSSProperties = { flexShrink: 0, fontSize: 24, fontWeight: 700, letterSpacing: 1, textAlign: 'center', color: '#fff', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
const center2Text: CSSProperties = { flexShrink: 0, fontSize: 13, fontWeight: 700, letterSpacing: 1, textAlign: 'center', color: '#fff', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
const bottomBand: CSSProperties = { display: 'flex', gap: 20, minHeight: 24, flexShrink: 0, alignItems: 'flex-end', padding: '0 12px 10px' }
const bottomLeftCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, width: '50%', minWidth: 0 }
const bottomRightCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, width: '50%', minWidth: 0, alignItems: 'flex-end' }
const bottomText: CSSProperties = { flexShrink: 0, fontSize: 15, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }

export default CardsVisual
